import { useState, useEffect } from 'react'
import { usePurchaseOrderDetailsQuery } from 'graphql/costcapture'
import { GRAPHQL_ENDPOINTS } from 'config/apollo-client'

type PurchaseOrderValidationResult = {
	isValidating: boolean,
	hasWarning: boolean,
	warningMessage?: string,
}

type UsePurchaseOrderValidationProps = {
	purchaseOrderId?: string,
	currentPartItemId?: string,
	enabled?: boolean,
}

export default function usePurchaseOrderValidation( {
	purchaseOrderId,
	currentPartItemId,
	enabled = true
}: UsePurchaseOrderValidationProps ): PurchaseOrderValidationResult {
	const [ validationResult, setValidationResult ] = useState<PurchaseOrderValidationResult>( {
		isValidating: false,
		hasWarning: false
	} )

	// Query purchase order details to get line items
	const { data, loading, error } = usePurchaseOrderDetailsQuery( {
		variables: { id: purchaseOrderId ?? '' },
		context: { endpoint: GRAPHQL_ENDPOINTS.COST_CAPTURE },
		skip: !purchaseOrderId || !enabled || !currentPartItemId
	} )

	useEffect( () => {
		if ( !purchaseOrderId || !currentPartItemId || !enabled ) {
			setValidationResult( {
				isValidating: false,
				hasWarning: false
			} )
			return
		}

		if ( loading ) {
			setValidationResult( {
				isValidating: true,
				hasWarning: false
			} )
			return
		}

		if ( error ) {
			// If we can't validate, don't show a warning - silently fail gracefully
			setValidationResult( {
				isValidating: false,
				hasWarning: false
			} )
			return
		}

		if ( data?.purchaseOrder ) {
			if ( data.purchaseOrder.lineItems && data.purchaseOrder.lineItems.length > 0 ) {
				// Check if any line item matches the current part's itemId
				const containsPart = data.purchaseOrder.lineItems.some(
					( lineItem ) => String( lineItem.itemId ) === String( currentPartItemId )
				)

				if ( !containsPart ) {
					setValidationResult( {
						isValidating: false,
						hasWarning: true,
						warningMessage: 'This purchase order doesn\'t contain the product you\'re adjusting. Are you sure this is correct?'
					} )
				} else {
					setValidationResult( {
						isValidating: false,
						hasWarning: false
					} )
				}
			} else {
				// Purchase order exists but has no line items - show warning
				setValidationResult( {
					isValidating: false,
					hasWarning: true,
					warningMessage: 'This purchase order has no line items. Are you sure this is correct?'
				} )
			}
		} else {
			setValidationResult( {
				isValidating: false,
				hasWarning: false
			} )
		}
	}, [ purchaseOrderId, currentPartItemId, enabled, data, loading, error ] )

	return validationResult
}
